import { useEffect, useState } from "react";
import { CustomModal, Button, FormError } from "../../shared";
import { HiOutlineFolder, HiOutlineChevronRight, HiOutlineCheck, HiOutlineDocument } from "react-icons/hi2";
import { cn } from "../../../lib/cn";

const FolderTreeNode = ({ node, level = 0, selectedId, onSelect, currentId }) => {
  const [expanded, setExpanded] = useState(true);
  const isSelected = selectedId === node.id;
  const isCurrent = currentId != null && currentId === node.id;
  const childFolders = node.children || [];
  const childFiles = node.files || [];
  const hasContents = childFolders.length > 0 || childFiles.length > 0;

  return (
    <div className="text-sm">
      <div
        className={cn(
          "flex items-center gap-2 rounded-lg px-2 py-1.5 transition-colors",
          isSelected ? "bg-secondary/10 ring-2 ring-secondary/60" : "hover:bg-surface-hover"
        )}
        style={{ paddingLeft: `${level * 16 + 8}px` }}
      >
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className={cn(
            "flex h-5 w-5 items-center justify-center rounded text-grey transition-transform",
            hasContents ? "opacity-100" : "invisible",
            expanded && "rotate-90"
          )}
          aria-label={expanded ? "Collapse" : "Expand"}
          aria-expanded={expanded}
        >
          <HiOutlineChevronRight className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={() => onSelect?.(node)}
          className="flex flex-1 items-center gap-2 text-left"
          aria-pressed={isSelected}
          aria-current={isCurrent ? "location" : undefined}
        >
          <HiOutlineFolder className="h-4 w-4 text-secondary-dark" />
          <span className={cn("truncate font-medium", isSelected ? "text-secondary-dark" : "text-darkgrey")}>
            {node.name}
          </span>
        </button>
        {isCurrent && (
          <span className="rounded-full bg-secondary/10 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-secondary-dark">
            Current location
          </span>
        )}
        {isSelected && !isCurrent && <HiOutlineCheck className="h-4 w-4 text-secondary-dark" aria-hidden="true" />}
      </div>
      {expanded && hasContents && (
        <div className="ml-3 border-l border-surface-border pl-1">
          {childFolders.map((child) => (
            <FolderTreeNode
              key={child.id}
              node={child}
              level={level + 1}
              selectedId={selectedId}
              onSelect={onSelect}
              currentId={currentId}
            />
          ))}
          {childFiles.map((file) => (
            <div
              key={`file-${file.id}`}
              className="flex items-center gap-2 rounded-lg px-2 py-1.5"
              style={{ paddingLeft: `${(level + 1) * 16 + 8}px` }}
              title={file.name}
            >
              <span className="h-5 w-5 shrink-0" aria-hidden="true" />
              <HiOutlineDocument className="h-4 w-4 shrink-0 text-grey" aria-hidden="true" />
              <span className="truncate text-grey">{file.name}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const MovePickerTree = ({ tree, selectedId, onSelect, currentId }) => {
  if (!tree) {
    return <p className="text-sm text-grey">No folders available.</p>;
  }
  return (
    <div className="max-h-72 overflow-y-auto rounded-xl border border-surface-border bg-white p-2">
      <FolderTreeNode
        node={tree}
        level={0}
        selectedId={selectedId}
        onSelect={onSelect}
        currentId={currentId}
      />
    </div>
  );
};

export const FolderCreateModal = ({ isOpen, onClose, onSubmit, currentFolderId, isLoading, errorMessage }) => {
  const [name, setName] = useState("");
  useEffect(() => {
    if (isOpen) {
      setName("");
    }
  }, [isOpen]);
  const trimmed = name.trim();
  const isValid = trimmed.length > 0 && !trimmed.includes("/") && trimmed.length <= 255;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isValid) return;
    onSubmit({ name: trimmed, parentId: currentFolderId ?? null });
  };

  return (
    <CustomModal
      title="New folder"
      open={isOpen}
      close={onClose}
      showPrimaryButton={false}
      showSecondaryButton={false}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="folder-name" className="mb-1 block text-xs font-semibold uppercase tracking-wider text-grey">
            Folder name
          </label>
          <input
            id="folder-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Specs"
            className="w-full rounded-lg border border-surface-border bg-white px-3 py-2 text-sm text-darkgrey outline-none focus:border-secondary/50 focus:ring-2 focus:ring-secondary/10"
            maxLength={255}
          />
          {name.length > 0 && !isValid && (
            <p className="mt-1 text-xs text-danger">Folder name cannot be empty or contain &quot;/&quot;.</p>
          )}
        </div>
        <FormError message={errorMessage} />
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outlined" color="secondary" size="md" rounded="xl" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="filled"
            color="secondary"
            size="md"
            rounded="xl"
            disabled={!isValid}
            isLoading={isLoading}
          >
            Create folder
          </Button>
        </div>
      </form>
    </CustomModal>
  );
};

export const FolderRenameModal = ({ isOpen, onClose, onSubmit, item, isLoading, errorMessage }) => {
  const [name, setName] = useState("");
  useEffect(() => {
    if (isOpen && item) {
      setName(item.name || "");
    }
  }, [isOpen, item]);
  const trimmed = name.trim();
  const isValid = trimmed.length > 0 && !trimmed.includes("/") && trimmed.length <= 255;
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isValid) return;
    onSubmit({ name: trimmed });
  };
  return (
    <CustomModal
      title={item?.kind === "folder" ? "Rename folder" : "Rename file"}
      open={isOpen}
      close={onClose}
      showPrimaryButton={false}
      showSecondaryButton={false}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="rename-input" className="mb-1 block text-xs font-semibold uppercase tracking-wider text-grey">
            New name
          </label>
          <input
            id="rename-input"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full rounded-lg border border-surface-border bg-white px-3 py-2 text-sm text-darkgrey outline-none focus:border-secondary/50 focus:ring-2 focus:ring-secondary/10"
            maxLength={255}
          />
          {name.length > 0 && !isValid && (
            <p className="mt-1 text-xs text-danger">Name cannot be empty or contain &quot;/&quot;.</p>
          )}
        </div>
        <FormError message={errorMessage} />
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outlined" color="secondary" size="md" rounded="xl" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="filled" color="secondary" size="md" rounded="xl" disabled={!isValid} loading={isLoading}>
            Save
          </Button>
        </div>
      </form>
    </CustomModal>
  );
};

export const FolderMoveModal = ({
  isOpen,
  onClose,
  onSubmit,
  item,
  tree,
  isLoading,
  errorMessage,
}) => {
  const [selectedId, setSelectedId] = useState(null);
  const currentId = item?.currentFolderId ?? null;
  const isFolderMove = item?.kind === "folder";
  const itemName = item?.name || item?.original_name;
  const title = itemName
    ? isFolderMove
      ? `Move folder "${itemName}"`
      : `Move "${itemName}"`
    : isFolderMove
      ? "Move folder"
      : "Move file";

  useEffect(() => {
    if (isOpen) {
      setSelectedId(item?.currentFolderId ?? tree?.id ?? null);
    }
  }, [isOpen, item, tree]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (selectedId == null || selectedId === currentId) return;
    onSubmit({ folderId: selectedId });
  };

  const noChange = selectedId != null && currentId != null && selectedId === currentId;
  const canSubmit = selectedId != null && !noChange;

  return (
    <CustomModal
      title={title}
      open={isOpen}
      close={onClose}
      showPrimaryButton={false}
      showSecondaryButton={false}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <p className="text-sm text-grey">
          {isFolderMove
            ? "Choose a new parent folder. This folder and its subfolders are hidden."
            : "Choose the destination folder for this file."}
        </p>
        <MovePickerTree
          tree={tree}
          selectedId={selectedId}
          onSelect={(node) => setSelectedId(node.id)}
          currentId={currentId}
        />
        {noChange && (
          <p className="text-xs text-grey">Select a different folder to continue.</p>
        )}
        <FormError message={errorMessage} />
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outlined" color="secondary" size="md" rounded="xl" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="filled"
            color="secondary"
            size="md"
            rounded="xl"
            disabled={!canSubmit}
            isLoading={isLoading}
          >
            Move
          </Button>
        </div>
      </form>
    </CustomModal>
  );
};

export const FolderDeleteModal = ({ isOpen, onClose, onConfirm, folder, isLoading, errorMessage }) => {
  if (!folder) return null;
  return (
    <CustomModal
      title="Delete folder"
      open={isOpen}
      close={onClose}
      showPrimaryButton={false}
      showSecondaryButton={false}
    >
      <div className="space-y-4">
        <p className="text-sm text-darkgrey">
          Delete <span className="font-semibold">{folder.name}</span> and everything inside it?
        </p>
        <p className="rounded-lg border border-danger/20 bg-danger/5 p-3 text-sm text-danger">
          All files and subfolders will be removed. This action cannot be undone.
        </p>
        <FormError message={errorMessage} />
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outlined" color="secondary" size="md" rounded="xl" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="filled"
            color="danger"
            size="md"
            rounded="xl"
            onClick={onConfirm}
            isLoading={isLoading}
          >
            Delete folder
          </Button>
        </div>
      </div>
    </CustomModal>
  );
};
